@let footerBGColorClass =
  styleService.getColorClass(config.config()?.footer?.backgroundColor ?? DEFAULT_CONFIG.footer?.backgroundColor, 'bg');
<footer
  role="contentinfo"
  [aria-label]="'footer.name' | translate"
  class="w-full"
  [ngClass]="[footerBGColorClass]"
>
  @let initData = informationService.init();
  @let company = companyInfo();
  @let chainstoresLink = system.getLink(initData?.linkTargets?.chainstores);
  <!-- Infoarea & Menuarea -->
  @let infoarea = configInfoarea();
  @let defaultInfoarea = DEFAULT_CONFIG.footer?.infoarea;
  @let menuarea = configMenuarea();
  @let defaultMenuarea = DEFAULT_CONFIG.footer?.menuarea;
  <!-- Padding — resolved by CSS so server and client render the same markup -->
  @let footerPadding = config.config()?.footer?.padding ?? DEFAULT_CONFIG.footer?.padding;
  <div
    class="xxl:grid-cols-[24rem_1fr] grid grid-cols-1 gap-10 lg:gap-12 xl:grid-cols-[18rem_1fr]"
    [ngClass]="[
      styleService.getColorClass(infoarea?.text?.color ?? defaultInfoarea?.text?.color, 'text'),
      styleService.getFontClasses(infoarea?.text?.font ?? defaultInfoarea?.text?.font),
      useContainer() ? 'container mx-auto' : ''
    ]"
    [gsResponsive]="{ padding: footerPadding }"
  >
    <!-- Company- / Contact-Info -->
    <div
      class="p flex flex-col gap-3 lg:gap-5"
      [gsResponsive]="{ 'font-size': infoarea?.text?.textSize ?? defaultInfoarea?.text?.textSize }"
    >
      <!-- LOGO -->
      @if (initData && initData.logo && infoarea?.logo?.active) {
        @let footerLogo = initData.logoAlternative ?? initData.logo;
        <a [href]="logoLink()" [attr.aria-label]="'header.aria.homepage' | translate" class="relative block h-16 w-32 lg:h-28 lg:w-48 xl:w-72">
          @for (p of [footerLogo]; track p?.medium ?? 'nopic') {
            <img
              class="object-contain object-left"
              [ngSrc]="imageService.getEndpoint(p, 'medium')!"
              [ngSrcset]="imageService.DEFAULT_SRCSET"
              [loaderParams]="imageService.getLoaderParams(p)"
              sizes="(max-width: 1024px) 128px, 288px"
              fill
              gsImageAccessibility
              [gsImgAlt]="'footer.infoarea.logo.alt' | translate"
              [gsImgSeo]="'footer.infoarea.logo.seo' | translate"
            />
          }
        </a>
      }
      @if (company && (infoarea?.chainstore?.active ?? defaultInfoarea?.chainstore?.active)) {
        <!-- Company - Name, Address, Contact -->
        @let companyName = company.contact?.company;
        @if (companyName && (infoarea?.chainstore?.showCompanyName ?? defaultInfoarea?.chainstore?.showCompanyName)) {
          <span
            class="mb-1.5 lg:mb-2"
            [ngClass]="[
              styleService.getColorClass(menuarea?.heading?.color ?? defaultMenuarea?.heading?.color, 'text'),
              styleService.getFontClasses(menuarea?.heading?.font ?? defaultMenuarea?.heading?.font)
            ]"
            [gsResponsive]="{ 'font-size': menuarea?.heading?.textSize ?? defaultMenuarea?.heading?.textSize }"
          >
            {{ companyName }}
          </span>
        }
        @let showAddress = infoarea?.chainstore?.showAddress ?? defaultInfoarea?.chainstore?.showAddress;
        @let showPhoneNumber = infoarea?.chainstore?.showPhoneNumber ?? defaultInfoarea?.chainstore?.showPhoneNumber;
        @let showEmail = infoarea?.chainstore?.showEmail ?? defaultInfoarea?.chainstore?.showEmail;
        @if (showEmail || showAddress || showPhoneNumber) {
          <span
            class="flex flex-col"
            [ngClass]="[
              styleService.getColorClass(infoarea?.text?.color ?? defaultInfoarea?.text?.color, 'text'),
              styleService.getFontClasses(infoarea?.text?.font ?? defaultInfoarea?.text?.font)
            ]"
          >
            @if (showAddress && (company.street || company.zip || company.city)) {
              @let fullAddress = `${company.street ?? ''}+${company.houseNumber ?? ''}+${company.zip ?? ''}+${company.city ?? ''}`;

              <a
                [href]="'https://www.google.com/maps/search/?api=1&query=' + fullAddress"
                target="_blank"
                class="w-fit pt-2 hover:underline"
              >
                <span class="sr-only">{{ 'footer.infoarea.chainstore.address' | translate }}:</span>
                {{ company.street }}
                {{ company.houseNumber }}
                <br />
                {{ company.zip }}
                {{ company.city }}
              </a>
            }
            @if (showPhoneNumber && company.phone) {
              <a class="w-fit hover:underline" [href]="'tel:' + company.phone">
                {{ 'footer.infoarea.chainstore.phone' | translate }}: {{ company.phone }}
              </a>
            }
            @if (showEmail && company.eMailAddress) {
              <a [href]="'mailto:' + company.eMailAddress" class="w-fit hover:underline">
                {{ 'footer.infoarea.chainstore.email' | translate }}: {{ company.eMailAddress }}
              </a>
            }
          </span>
        }
        <!-- Openingtimes -->

        @let showOpeningHours = infoarea?.chainstore?.showOpeningHours ?? defaultInfoarea?.chainstore?.showOpeningHours;
        @let openingHoursInfo = openingHours();
        @let showSpecialOpeningHours = infoarea?.chainstore?.showSpecialOpeningHours ?? defaultInfoarea?.chainstore?.showSpecialOpeningHours;
        @let specialOpeningGroups = showSpecialOpeningHours ? specialOpeningTimeGroups() : [];
        @if (showOpeningHours && company && (openingHoursInfo.length > 0 || specialOpeningGroups.length > 0)) {
          <ul
            [ngClass]="[
              styleService.getColorClass(infoarea?.text?.color ?? defaultInfoarea?.text?.color, 'text'),
              styleService.getFontClasses(infoarea?.text?.font ?? defaultInfoarea?.text?.font)
            ]"
          >
            <!-- Default Opening Times -->
            @if (openingHoursInfo && openingHoursInfo.length > 0) {
              @for (item of openingHoursInfo; track $index) {
                <li class="grid grid-cols-2 gap-1">
                  <strong class="font-bold">{{ item.openingTimeName || item.title }}:</strong>
                  <span class="whitespace-pre-line">
                    @if (item.closed) {
                      {{ 'footer.infoarea.chainstore.closed' | translate }}
                    } @else if (item.formattedRange) {
                      {{ item.formattedRange }}
                    }
                  </span>
                  @if (item.info) {
                    <span class="col-span-2">{{ item.info }}</span>
                  }
                </li>
              }
            }
            <!-- Special Opening Times -->
            @for (group of specialOpeningGroups; track $index) {
              <li class="mt-4 grid grid-cols-2 gap-1">
                <strong class="col-span-2 font-bold">{{ group.label }}</strong>
                <span class="col-span-2">
                  {{ group.startDate | date: 'shortDate' }}
                  @if (group.endDate && group.endDate.getTime() !== group.startDate.getTime()) {
                    - {{ group.endDate | date: 'shortDate' }}
                  }
                </span>
                @for (entry of group.entries; track $index) {
                  @let hasDisplayRange = (entry.closed && !entry.hideClosedText) || !!entry.formattedRange;
                  @let showDayLabel = hasDisplayRange && entry.title && entry.title !== group.label;
                  @if (showDayLabel) {
                    <strong class="font-bold">{{ entry.title }}:</strong>
                    <span class="whitespace-pre-line">
                      @if (entry.formattedRange) {
                        {{ entry.formattedRange }}
                      } @else if (entry.closed) {
                        {{ 'footer.infoarea.chainstore.closed' | translate }}
                      }
                    </span>
                  } @else if (hasDisplayRange) {
                    <span class="whitespace-pre-line" [class.col-span-2]="!showDayLabel">
                      @if (entry.closed) {
                        {{ 'footer.infoarea.chainstore.closed' | translate }}
                      } @else if (entry.formattedRange) {
                        {{ entry.formattedRange }}
                      }
                    </span>
                  }
                  @if (entry.info) {
                    <span class="col-span-1">{{ entry.info }}</span>
                  }
                }
              </li>
            }
            <!-- All chainstores link -->
            @if (chainstoresLink && (infoarea?.chainstore?.showAllChainstoresLink ?? defaultInfoarea?.chainstore?.showAllChainstoresLink)) {
              <a [href]="chainstoresLink" class="font-medium md:font-light">
                {{ infoarea?.chainstore?.allChainstoresLinkText?.trim() || ('footer.infoarea.chainstore.viewAllLocations' | translate) }}
                <gs-icon iconName="arrow-right" iconWeight="light" class="text-sm"></gs-icon>
              </a>
            }
          </ul>
        }
      }
    </div>
    <!-- Menuarea -->
    @let menuItemGap = menuarea?.gapBetweenItems ?? defaultMenuarea?.gapBetweenItems ?? 8;
    <span
      class="flex flex-row flex-wrap items-start justify-start gap-8 lg:grid lg:auto-rows-min lg:gap-12"
      [ngClass]="[
        styleService.getColorClass(menuarea?.items?.color ?? defaultMenuarea?.items?.color, 'text'),
        styleService.getFontClasses(menuarea?.items?.font ?? defaultMenuarea?.items?.font)
      ]"
      [style.grid-template-columns]="menuareaGridColumns()"
      [gsResponsive]="{ 'font-size': menuarea?.items?.textSize ?? defaultMenuarea?.items?.textSize }"
    >
      <!-- iD is no primary-key here... Dont ask.. ✨The Backend✨ -->
      @for (menu of footerMenu(); track $index) {
        <span class="flex w-44 flex-col lg:w-52 xl:w-60" [style.gap.px]="menuItemGap">
          @if (initData && initData.logo && infoarea?.logo?.active) {
            <div class="h-1 md:h-28" aria-hidden="true"></div>
          }
          @if (menu.name && menu.name.trim().length > 0) {
            @let headingClasses =
              [
                styleService.getColorClass(menuarea?.heading?.color ?? defaultMenuarea?.heading?.color, 'text'),
                styleService.getFontClasses(menuarea?.heading?.font ?? defaultMenuarea?.heading?.font)
              ];
            @let headingTextSize = { 'font-size': menuarea?.heading?.textSize ?? defaultMenuarea?.heading?.textSize };
            @let menuUrl = menu.url?.trim() ?? '';
            <!-- Branch on the raw url (not the resolved link) so server and client render the same structure (SSR hydration) -->
            @if (menuUrl) {
              @let menuIsExternal = menuUrl?.toLowerCase()?.startsWith('http');
              <a
                [attr.href]="system.getLink(menuUrl) || null"
                [target]="menuIsExternal ? '_blank' : '_self'"
                [attr.rel]="menuIsExternal ? 'noopener noreferrer' : null"
                class="mb-1.5 hover:underline lg:mb-2"
                [ngClass]="headingClasses"
                [gsResponsive]="headingTextSize"
              >
                {{ menu.name }}
              </a>
            } @else {
              <span class="mb-1.5 lg:mb-2" [ngClass]="headingClasses" [gsResponsive]="headingTextSize">
                {{ menu.name }}
              </span>
            }
          }
          @for (item of menu.nodes; track $index) {
            @let itemUrl = item.url?.trim() ?? '';
            <!-- Branch on the raw url (not the resolved link) so server and client render the same structure (SSR hydration) -->
            @if (itemUrl) {
              @let isExternal = itemUrl.toLowerCase().startsWith('http');
              <a
                [attr.href]="system.getLink(itemUrl) || null"
                [target]="isExternal ? '_blank' : '_self'"
                [attr.rel]="isExternal ? 'noopener noreferrer' : null"
                class="hover:underline"
              >
                {{ item.name }}
              </a>
            } @else {
              <span>{{ item.name }}</span>
            }
          }
        </span>
      }
    </span>
    <!-- Blocks below the menu — order comes from footer.blockOrder -->
    <!-- Grid mirrors the infoarea + menuarea columns, so a wrap starts back at the infoarea column -->
    <!-- Infoarea widths stay in Tailwind so they follow the same breakpoints as the footer grid above -->
    <span
      class="xl:grid-cols-[18rem_repeat(var(--footer-block-cols),minmax(0,1fr))] xxl:grid-cols-[24rem_repeat(var(--footer-block-cols),minmax(0,1fr))] flex flex-row flex-wrap items-start gap-8 lg:gap-12 xl:col-span-2 xl:grid xl:auto-rows-min"
      [style]="{ '--footer-block-cols': footerBlockColumns() }"
    >
      @for (block of footerBlocks(); track block) {
        <span class="flex flex-col">
          <ng-container [ngTemplateOutlet]="footerBlock" [ngTemplateOutletContext]="{ $implicit: block }"></ng-container>
        </span>
      }
    </span>
    <!-- Gesetzlicher Gewährleistungshinweis (EmpCo) — bewusst fest eingebaut und kein FooterBlock: weder abschaltbar noch wegsortierbar -->
    <section class="flex flex-col gap-3 lg:gap-5 xl:col-span-2" [attr.aria-label]="'footer.warranty.title' | translate">
      <span
        [ngClass]="[
          styleService.getColorClass(menuarea?.heading?.color ?? defaultMenuarea?.heading?.color, 'text'),
          styleService.getFontClasses(menuarea?.heading?.font ?? defaultMenuarea?.heading?.font)
        ]"
        [gsResponsive]="{ 'font-size': menuarea?.heading?.textSize ?? defaultMenuarea?.heading?.textSize }"
      >
        {{ 'footer.warranty.title' | translate }}
      </span>
      <!-- Text in der Farbe/Schrift der Menüpunkte, damit er ohne eigene Einstellung zum Footer passt -->
      <p
        class="p"
        [ngClass]="[
          styleService.getColorClass(menuarea?.items?.color ?? defaultMenuarea?.items?.color, 'text'),
          styleService.getFontClasses(menuarea?.items?.font ?? defaultMenuarea?.items?.font)
        ]"
        [gsResponsive]="{ 'font-size': menuarea?.items?.textSize ?? defaultMenuarea?.items?.textSize }"
      >
        {{ 'footer.warranty.text' | translate }}
        @if (footerService.warrantyLink(); as warrantyLink) {
          <a [href]="warrantyLink" class="underline hover:no-underline focus:no-underline">
            {{ 'footer.warranty.link' | translate }}
            <span class="sr-only">{{ 'footer.warranty.linkHint' | translate }}</span>
          </a>
        }
      </p>
    </section>
  </div>
  <!-- Bottombar -->
  @let bottombar = configBottombar();
  @let defaultBottombar = DEFAULT_CONFIG.footer?.bottombar;
  @if (bottombar?.active ?? defaultBottombar?.active) {
    <div
      class="flex w-full flex-col items-center justify-between gap-3 py-5 lg:flex-row-reverse lg:py-3"
      [ngClass]="[
        styleService.getColorClass(bottombar?.backgroundColor ?? defaultBottombar?.backgroundColor, 'bg'),
        styleService.getColorClass(bottombar?.text?.color ?? defaultBottombar?.text?.color, 'text'),
        styleService.getFontClasses(bottombar?.text?.font ?? defaultBottombar?.text?.font)
      ]"
      [gsResponsive]="{ 'font-size': bottombar?.text?.textSize ?? defaultBottombar?.text?.textSize }"
    >
      <div
        class="flex w-full flex-col items-center justify-between gap-3 lg:flex-row-reverse"
        [gsResponsive]="{ 'padding-inline': footerPadding }"
        [ngClass]="{ 'container mx-auto': useContainer() }"
      >
        <!-- Links -->
        <span class="flex flex-wrap items-center justify-center gap-2 gap-y-0.5 md:gap-6 lg:justify-start xl:gap-10">
          @if (footerService.cookieBannerActive) {
            <button
              data-cc="show-preferencesModal"
              (click)="footerService.showCookiePreferences()"
              [attr.aria-label]="'footer.bottombar.cookieSettings' | translate"
              class="hover:underline"
            >
              {{ 'footer.bottombar.cookieSettings' | translate }}
            </button>
          }
          @for (item of initData?.menus?.['cs4-footerCopyright']; track item.name) {
            @let itemUrl = item.url?.trim() ?? '';
            <!-- Branch on the raw url (not the resolved link) so server and client render the same structure (SSR hydration) -->
            @if (itemUrl) {
              @let isExternal = itemUrl.toLowerCase().startsWith('http');
              <a
                [attr.href]="system.getLink(itemUrl) || null"
                [target]="isExternal ? '_blank' : '_self'"
                [attr.rel]="isExternal ? 'noopener noreferrer' : null"
                class="hover:underline"
              >
                {{ item.name }}
              </a>
            } @else {
              <span>{{ item.name }}</span>
            }
          }
        </span>
        <!-- Copyright -->
        @let companyName = companyInfo()?.contact?.company;
        @if (companyName) {
          <div class="text-center lg:text-start">© {{ footerService.currentYear }} {{ companyName }}</div>
        }
      </div>
    </div>
  }
  <ng-template #revocationBlock>
    <div class="flex flex-col gap-3 lg:gap-5">
      <span
        [ngClass]="[
          styleService.getColorClass(menuarea?.heading?.color ?? defaultMenuarea?.heading?.color, 'text'),
          styleService.getFontClasses(menuarea?.heading?.font ?? defaultMenuarea?.heading?.font)
        ]"
        [gsResponsive]="{ 'font-size': menuarea?.heading?.textSize ?? defaultMenuarea?.heading?.textSize }"
      >
        {{ 'footer.revocation.title' | translate }}
      </span>
      <a
        [href]="footerService.revocationLink()"
        [ngClass]="[footerService.revocationButtonClasses]"
        [ngStyle]="footerService.revocationButtonStyles"
        class="inline-block w-fit text-center duration-200"
      >
        {{ 'footer.revocation.button' | translate }}
      </a>
    </div>
  </ng-template>
  <ng-template #paymentMethodsBlock>
    <span class="flex flex-col gap-3 lg:gap-5">
      <span
        [ngClass]="[
          styleService.getColorClass(menuarea?.heading?.color ?? defaultMenuarea?.heading?.color, 'text'),
          styleService.getFontClasses(menuarea?.heading?.font ?? defaultMenuarea?.heading?.font)
        ]"
        [gsResponsive]="{ 'font-size': menuarea?.heading?.textSize ?? defaultMenuarea?.heading?.textSize }"
      >
        {{ 'footer.infoarea.paymentMethods.label' | translate }}
      </span>
      <span class="flex flex-row flex-wrap gap-2 lg:gap-3">
        @for (paymentMethod of paymentMethods(); track paymentMethod.paymentMethodID) {
          @if (paymentMethod.picture; as picture) {
            @for (p of [picture]; track p?.medium ?? 'nopic') {
              <img
                class="h-8 w-auto object-contain lg:h-10"
                [ngSrc]="imageService.getEndpoint(p, 'medium')!"
                [ngSrcset]="imageService.DEFAULT_SRCSET"
                [loaderParams]="imageService.getLoaderParams(p)"
                sizes="(max-width: 1024px) 32px, 40px"
                [width]="imageService.getIntrinsicWidth(p, 'medium')"
                [height]="imageService.getIntrinsicHeight(p, 'medium')"
                gsImageAccessibility
                [gsImgAlt]="paymentMethod.name"
                [gsImgSeo]="paymentMethod.name"
              />
            }
          }
        }
      </span>
    </span>
  </ng-template>
  <ng-template #deliveryMethodsBlock>
    <span class="flex flex-col gap-3 lg:gap-5">
      <span
        [ngClass]="[
          styleService.getColorClass(menuarea?.heading?.color ?? defaultMenuarea?.heading?.color, 'text'),
          styleService.getFontClasses(menuarea?.heading?.font ?? defaultMenuarea?.heading?.font)
        ]"
        [gsResponsive]="{ 'font-size': menuarea?.heading?.textSize ?? defaultMenuarea?.heading?.textSize }"
      >
        {{ 'footer.infoarea.deliveryMethods.label' | translate }}
      </span>
      <span class="flex flex-row flex-wrap gap-2 lg:gap-3">
        @for (shippingMethod of shippingMethods(); track shippingMethod.shippingMethodID) {
          @if (shippingMethod.picture; as picture) {
            @for (p of [picture]; track p?.medium ?? 'nopic') {
              <img
                class="h-8 w-auto object-contain lg:h-10"
                [ngSrc]="imageService.getEndpoint(p, 'medium')!"
                [ngSrcset]="imageService.DEFAULT_SRCSET"
                [loaderParams]="imageService.getLoaderParams(p)"
                sizes="(max-width: 1024px) 32px, 40px"
                [width]="imageService.getIntrinsicWidth(p, 'medium')"
                [height]="imageService.getIntrinsicHeight(p, 'medium')"
                gsImageAccessibility
                [gsImgAlt]="shippingMethod.name"
                [gsImgSeo]="shippingMethod.name"
              />
            }
          }
        }
      </span>
    </span>
  </ng-template>
  <ng-template #socialMediaLinksBlock>
    <span class="flex flex-col gap-3 lg:gap-5">
      <span
        [ngClass]="[
          styleService.getColorClass(menuarea?.heading?.color ?? defaultMenuarea?.heading?.color, 'text'),
          styleService.getFontClasses(menuarea?.heading?.font ?? defaultMenuarea?.heading?.font)
        ]"
        [gsResponsive]="{ 'font-size': menuarea?.heading?.textSize ?? defaultMenuarea?.heading?.textSize }"
      >
        {{ 'footer.infoarea.socialMediaLinks.label' | translate }}
      </span>
      <span class="flex flex-row flex-wrap gap-2 lg:gap-3">
        @for (socialMedia of socialMediaLinks() | keyvalue; track socialMedia.key) {
          @if (socialMedia.value) {
            <a [href]="socialMedia.value" target="_blank" rel="noopener noreferrer" class="size-8 lg:size-9">
              @let socialMediaKey = socialMedia.key;
              @let socialIconName = SOCIAL_MEDIA_ICON_MAP[socialMediaKey];
              <gs-icon
                [iconName]="socialIconName ?? socialMediaKey.toLowerCase()"
                iconWeight="regular"
                [ngClass]="[
                  styleService.getColorClass(infoarea?.socialMediaLinks?.color ?? defaultInfoarea?.socialMediaLinks?.color, 'text')
                ]"
                class="text-3xl lg:text-4xl"
              />
            </a>
          }
        }
      </span>
    </span>
  </ng-template>
  <ng-template #appLinksBlock>
    <span class="flex flex-col gap-3 lg:gap-5">
      <span
        [ngClass]="[
          styleService.getColorClass(menuarea?.heading?.color ?? defaultMenuarea?.heading?.color, 'text'),
          styleService.getFontClasses(menuarea?.heading?.font ?? defaultMenuarea?.heading?.font)
        ]"
        [gsResponsive]="{ 'font-size': menuarea?.heading?.textSize ?? defaultMenuarea?.heading?.textSize }"
      >
        {{ 'footer.infoarea.appLinks.label' | translate }}
      </span>
      <span class="flex flex-row flex-wrap gap-2 lg:gap-3">
        @for (app of appLinks() | keyvalue; track app.key) {
          @if (app.value?.trim()) {
            <a [href]="app.value" target="_blank" rel="noopener noreferrer">
              <img
                class="h-8 w-auto object-contain lg:h-10"
                style="aspect-ratio: 173 / 50"
                [src]="`assets/images/appbutton/${app.key}.svg`"
                [alt]="app.key"
                gsImageAccessibility
                [gsImgAlt]="app.key"
                [gsImgSeo]="app.key"
              />
            </a>
          }
        }
      </span>
    </span>
  </ng-template>
  <ng-template #footerBlock let-block>
    @switch (block) {
      @case ('paymentMethods') {
        <ng-container [ngTemplateOutlet]="paymentMethodsBlock"></ng-container>
      }
      @case ('deliveryMethods') {
        <ng-container [ngTemplateOutlet]="deliveryMethodsBlock"></ng-container>
      }
      @case ('socialMediaLinks') {
        <ng-container [ngTemplateOutlet]="socialMediaLinksBlock"></ng-container>
      }
      @case ('appLinks') {
        <ng-container [ngTemplateOutlet]="appLinksBlock"></ng-container>
      }
      @case ('revocation') {
        <ng-container [ngTemplateOutlet]="revocationBlock"></ng-container>
      }
    }
  </ng-template>
</footer>
